/**
 * Copyright (c) 2026 Spicy Friends Ltd.
 * All rights reserved.
 *
 * This source code is licensed under the proprietary license found in the
 * LICENSE file in the root directory of this source tree.
 */

'use client'

import { StarIcon } from 'lucide-react'
import { cva, type VariantProps } from 'class-variance-authority'
import type { ComponentPropsWithoutRef } from 'react'

import { cn } from '../../lib/utils'

import { Button } from './button'

const ratingVariants = cva('pointer-events-none shrink-0', {
  variants: {
    variant: {
      default: '',
      primary: '',
      secondary: '',
      muted: '',
    },
    filled: {
      true: '',
      false: 'text-muted-foreground',
    },
    size: {
      xs: 'size-4',
      sm: 'size-5',
      default: 'size-6',
      lg: 'size-8',
    },
  },
  compoundVariants: [
    { variant: 'default', filled: true, class: 'fill-amber-400 text-amber-400' },
    { variant: 'primary', filled: true, class: 'fill-primary text-primary' },
    {
      variant: 'secondary',
      filled: true,
      class: 'fill-secondary-foreground text-secondary-foreground',
    },
    { variant: 'muted', filled: true, class: 'fill-foreground text-foreground' },
  ],
  defaultVariants: {
    variant: 'default',
    filled: false,
    size: 'default',
  },
})

const ratingButtonVariants = cva('p-0', {
  variants: {
    size: {
      xs: 'size-4',
      sm: 'size-5',
      default: 'size-6',
      lg: 'size-8',
    },
  },
  defaultVariants: {
    size: 'default',
  },
})

export type RatingProps = {
  /** Number of stars (default 5). */
  count?: number
  disabled?: boolean
  onBlur?: () => void
  onChange?: (value: number) => void
  value: number
} & VariantProps<typeof ratingVariants> &
  Omit<ComponentPropsWithoutRef<'div'>, 'onChange'>

function Rating({
  className,
  count = 5,
  disabled,
  onBlur,
  onChange,
  size = 'default',
  value,
  variant = 'default',
  ...props
}: RatingProps) {
  const clamped = Math.min(Math.max(0, value), count)

  return (
    <div className={cn('flex items-center gap-1', className)} {...props}>
      {Array.from({ length: count }).map((_, index) => {
        const starValue = index + 1
        const filled = starValue <= clamped
        const iconClass = ratingVariants({ variant, filled, size })
        const buttonClass = ratingButtonVariants({ size })

        if (onChange) {
          return (
            <Button
              aria-label={`${starValue} of ${count}`}
              className={cn(buttonClass)}
              disabled={disabled}
              key={starValue}
              onBlur={onBlur}
              onClick={() => onChange(starValue)}
              type="button"
              variant="ghost"
            >
              <StarIcon className={iconClass} />
            </Button>
          )
        }

        return (
          <span aria-hidden className={cn('inline-flex', buttonClass)} key={starValue}>
            <StarIcon className={iconClass} />
          </span>
        )
      })}
    </div>
  )
}

export { Rating, ratingVariants }
